"use client"

import { useEffect, useState, type ComponentProps, type ComponentType } from "react"
import { LocationSearchButton } from "./LocationSearchButton"
import type { LocationSearchPanel as LocationSearchPanelComponent } from "./LocationSearchPanel"

export interface LocationSearchItem {
  displayName: string
  href: string
}

interface LocationSearchProps {
  locations: LocationSearchItem[]
  placeholder: string
  emptyMessage: string
  size?: "default" | "lg"
}

type PanelComponent = ComponentType<
  ComponentProps<typeof LocationSearchPanelComponent>
>

export function LocationSearch(props: LocationSearchProps) {
  const [Panel, setPanel] = useState<PanelComponent | null>(null)
  const [activated, setActivated] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      void import("./LocationSearchPanel").then((m) => {
        if (!cancelled) setPanel(() => m.LocationSearchPanel)
      })
    }
    const w = window as Window & {
      requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number
    }
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(load, { timeout: 3000 })
      return () => {
        cancelled = true
      }
    }
    const t = setTimeout(load, 1500)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [])

  if (activated && Panel) {
    return <Panel {...props} initialOpen />
  }

  const handleActivate = () => {
    setActivated(true)
    if (!Panel) {
      void import("./LocationSearchPanel").then((m) => {
        setPanel(() => m.LocationSearchPanel)
      })
    }
  }

  return (
    <LocationSearchButton
      placeholder={props.placeholder}
      size={props.size}
      aria-expanded={false}
      onClick={handleActivate}
      onFocus={handleActivate}
    />
  )
}
